import { useState, useMemo } from 'react'
import { CalendarCheck } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useCollection } from '../../hooks/useFirestore'
import { formatDate, groupBy, todayISO } from '../../utils/helpers'
import { Badge, Card } from '../../components/common'

// One tab for the worker's own schedule, plus a roster tab per department
const WorkersSchedule = () => {
  const { user, userProfile } = useAuth()
  const myDepartments = userProfile?.departmentIds || []
  const [tab, setTab] = useState('mine')

  const today = todayISO()

  const { data: list, loading } = useCollection('assignments', {
    orderByField: 'date',
    orderDirection: 'asc',
    whereClauses: tab !== 'mine'
      ? [
          { field: 'departmentId', operator: '==', value: tab },
          { field: 'date', operator: '>=', value: today },
        ]
      : [
          { field: 'workerUid', operator: '==', value: user?.uid },
          { field: 'date', operator: '>=', value: today },
        ],
  })

  const { data: departments } = useCollection('departments', {
    orderByField: 'order',
    orderDirection: 'asc',
  })

  const byDate = useMemo(() => groupBy(list || [], 'date'), [list])
  const dates = Object.keys(byDate).sort()

  const deptName = (slug) => departments?.find((d) => d.id === slug)?.name || slug

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-bold text-dark-100">Schedule</h1>
        <p className="text-gray-500">Upcoming services and who's serving</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto">
        <button
          onClick={() => setTab('mine')}
          className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
            tab === 'mine'
              ? 'bg-primary-900 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          My Schedule
        </button>
        {myDepartments.map((slug) => (
          <button
            key={slug}
            onClick={() => setTab(slug)}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              tab === slug
                ? 'bg-primary-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {deptName(slug)}
          </button>
        ))}
      </div>

      {/* Assignments grouped by date */}
      <div className="space-y-4">
        {dates.map((date) => (
          <div key={date} className="bg-white rounded-xl shadow-soft overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
              <p className="font-heading font-semibold text-dark-100">
                {formatDate(date, 'EEEE, MMMM d, yyyy')}
              </p>
            </div>
            <div className="divide-y divide-gray-100">
              {byDate[date].map((a) => (
                <div key={a.id} className="px-5 py-3 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-dark-100">
                      {tab === 'mine' ? a.serviceName : a.workerName}
                    </p>
                    <p className="text-sm text-gray-600">
                      {tab === 'mine'
                        ? [a.serviceTime, a.duty].filter(Boolean).join(' · ') || '—'
                        : [a.serviceName, a.serviceTime, a.duty].filter(Boolean).join(' · ')}
                    </p>
                    {a.notes && <p className="text-sm text-gray-400">{a.notes}</p>}
                  </div>
                  <Badge variant="primary" size="sm">{a.departmentName}</Badge>
                </div>
              ))}
            </div>
          </div>
        ))}

        {!loading && dates.length === 0 && (
          <Card className="text-center py-12 text-gray-500">
            <CalendarCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No upcoming assignments{tab !== 'mine' ? ' for this department' : ''}</p>
          </Card>
        )}
      </div>
    </div>
  )
}

export default WorkersSchedule
